import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend as RLegend, PieChart, Pie, Cell, LabelList, CartesianGrid } from 'recharts'
import { ArrowUp, ArrowDown, Minus, FileSpreadsheet, Printer } from 'lucide-react'
import { api } from '../../utils/api'
import CheckGrid from './CheckGrid'
import { ChartCard, Rules, STATE_STYLE, fmtMonth, fmtDay, downloadDataUrl, errMsg } from './shared'

interface Kpi { value: number | null; prev?: number | null }
export interface DashData {
  filters: { year: number; month: number; range: number; groupId: string | null; companyId: string | null }
  window: { from: string; to: string; monthFrom: string; monthTo: string }
  today: string; rules: Rules
  groups: { id: string; name: string; kind: string; frequency: string }[]
  kpi: {
    completeness: Kpi & { checked: number; required: number; ok: number; issue: number; missing: number }
    openIssues: Kpi; backupMissed: Kpi; todayMissing: Kpi
  }
  daily: { d: string; ok: number; issue: number; missing: number }[]; granularity: 'day' | 'week'
  topIssues: { itemId: string; name: string; host: string | null; group: string; issues: number; checked: number }[]
  uptime: { itemId: string; name: string; host: string | null; group: string; ok: number; issues: number; uptime: number | null }[]
  backup: {
    overall: { ok: number; issue: number; missing: number; rate: number | null }
    byFreq: { freq: string; ok: number; issue: number; missing: number; rate: number | null }[]
    latest: { itemId: string; name: string; group: string; freq: string; lastOk: string | null; lastChecked: string | null; consecutiveFail: number }[]
  }
  monthly: { month: string; ok: number; issue: number; missing: number; required: number; completeness: number | null }[]
  timeline: { itemId: string; itemName: string; hostName: string | null; groupName: string; from: string; to: string; length: number; label: string | null; ongoing: boolean }[]
}

const C = { ok: '#16a34a', issue: '#dc2626', missing: '#f59e0b', neutral: '#2563eb' }
const FREQ_TH: Record<string, string> = { DAILY: 'รายวัน', WEEKLY: 'รายสัปดาห์', MONTHLY: 'รายเดือน' }
const pctStr = (v: number | null | undefined) => (v == null ? '–' : `${v}%`)

function Delta({ cur, prev, goodWhenUp, unit = '' }: { cur: number | null; prev: number | null | undefined; goodWhenUp: boolean; unit?: string }) {
  if (cur == null || prev == null) return <span className="text-xs text-gray-400">ไม่มีข้อมูลเดือนก่อน</span>
  const d = Math.round((cur - prev) * 10) / 10
  if (d === 0) return <span className="text-xs text-gray-500 inline-flex items-center gap-0.5"><Minus size={12}/> เท่าเดือนก่อน</span>
  const good = (d > 0) === goodWhenUp
  return (
    <span className={`text-xs inline-flex items-center gap-0.5 font-medium ${good ? 'text-green-700' : 'text-red-600'}`}>
      {d > 0 ? <ArrowUp size={12}/> : <ArrowDown size={12}/>} {Math.abs(d)}{unit} <span className="font-normal">({good ? 'ดีขึ้น' : 'แย่ลง'}) จากเดือนก่อน</span>
    </span>
  )
}

function KpiCard({ title, value, sub, rule, children }: { title: string; value: string; sub?: string; rule: string; children?: React.ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4" title={rule}>
      <p className="text-xs text-gray-500 mb-1 cursor-help">{title} ⓘ</p>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

const Empty = ({ text = 'ไม่มีข้อมูลในช่วงที่เลือก' }) => <p className="text-sm text-gray-400 py-10 text-center">{text}</p>

/** KPI cards + all charts. Shared by the dashboard tab and the printable report. */
export function DashboardBody({ d }: { d: DashData }) {
  const k = d.kpi
  const win = d.window
  const days = Math.max(1, (Date.parse(win.to) - Date.parse(win.from)) / 86_400_000 + 1)
  const pos = (ymd: string) => Math.max(0, Math.min(100, ((Date.parse(ymd) - Date.parse(win.from)) / 86_400_000 / days) * 100))

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <KpiCard title="% ตรวจครบของเดือนนี้" value={pctStr(k.completeness.value)} rule={d.rules.completeness}
          sub={`ตรวจแล้ว ${k.completeness.checked}/${k.completeness.required} ช่อง · ผ่าน ${k.completeness.ok} · ผิดปกติ ${k.completeness.issue} · ยังไม่ตรวจ ${k.completeness.missing}`}>
          <Delta cur={k.completeness.value} prev={k.completeness.prev} goodWhenUp unit=" จุด"/>
        </KpiCard>
        <KpiCard title="รายการผิดปกติที่ค้างอยู่" value={String(k.openIssues.value ?? 0)} rule={d.rules.openIssues} sub="ผลตรวจล่าสุดยังเป็นผิดปกติ">
          <Delta cur={k.openIssues.value} prev={k.openIssues.prev} goodWhenUp={false}/>
        </KpiCard>
        <KpiCard title="Backup ที่พลาดในเดือนนี้" value={String(k.backupMissed.value ?? 0)} rule={d.rules.backup} sub="จำนวนช่อง backup ที่ไม่สำเร็จ">
          <Delta cur={k.backupMissed.value} prev={k.backupMissed.prev} goodWhenUp={false}/>
        </KpiCard>
        <KpiCard title="ยังไม่ได้ตรวจวันนี้" value={k.todayMissing.value == null ? '–' : String(k.todayMissing.value)} rule="นับเฉพาะรายการรายวันที่ต้องตรวจวันนี้และยังไม่มีผล (ไม่นับรายการที่ยังไม่เริ่ม/ยกเลิกใช้งาน)"
          sub={k.todayMissing.value == null ? 'แสดงเฉพาะเมื่อเลือกเดือนปัจจุบัน' : 'รายการ'}/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={`ผ่าน / ผิดปกติ / ยังไม่ได้ตรวจ ต่อ${d.granularity === 'week' ? 'สัปดาห์' : 'วัน'}`} rule="นับช่องของรายการรายวัน แยกสามสถานะ ไม่รวมกัน">
          {d.daily.length === 0 ? <Empty/> : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={d.daily} margin={{ left: -20, right: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="d" tickFormatter={v => v.slice(d.granularity === 'week' ? 5 : 8)} fontSize={10}/>
                  <YAxis fontSize={10} allowDecimals={false}/>
                  <Tooltip labelFormatter={v => fmtDay(String(v))}/>
                  <RLegend formatter={v => `${v === 'ok' ? '✓ ผ่าน' : v === 'issue' ? '▲ ผิดปกติ' : '○ ยังไม่ได้ตรวจ'}`}/>
                  <Bar dataKey="ok" stackId="a" fill={C.ok} name="ok"/>
                  <Bar dataKey="issue" stackId="a" fill={C.issue} name="issue"/>
                  <Bar dataKey="missing" stackId="a" fill={C.missing} name="missing"/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Top รายการที่ผิดปกติบ่อยสุด" rule="จำนวนครั้งที่ตรวจแล้วผิดปกติในช่วงที่เลือก">
          {d.topIssues.length === 0 ? <Empty text="ไม่พบรายการผิดปกติในช่วงที่เลือก ✓"/> : (
            <div style={{ height: Math.max(180, d.topIssues.length * 32 + 20) }}>
              <ResponsiveContainer>
                <BarChart data={d.topIssues.map(t => ({ ...t, label: t.host ? `${t.host} › ${t.name}` : t.name }))} layout="vertical" margin={{ left: 10, right: 30 }}>
                  <XAxis type="number" hide allowDecimals={false}/>
                  <YAxis type="category" dataKey="label" width={130} fontSize={11} tickFormatter={(v: string) => v.length > 20 ? v.slice(0, 19) + '…' : v}/>
                  <Tooltip formatter={(v: any, _n, p: any) => [`${v} ครั้ง (ตรวจแล้ว ${p.payload.checked} ช่อง)`, 'ผิดปกติ']}/>
                  <Bar dataKey="issues" fill={C.issue}><LabelList dataKey="issues" position="right" fontSize={11}/></Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Server uptime % (ต่ำ → สูง)" rule={d.rules.uptime}>
          {d.uptime.length === 0 ? <Empty text="ยังไม่มีข้อมูลจาก Group ประเภท Server"/> : (
            <div style={{ height: Math.max(180, d.uptime.length * 28 + 20) }}>
              <ResponsiveContainer>
                <BarChart data={d.uptime.map(u => ({ ...u, label: u.host ? `${u.host} › ${u.name}` : u.name }))} layout="vertical" margin={{ left: 10, right: 40 }}>
                  <XAxis type="number" domain={[0, 100]} hide/>
                  <YAxis type="category" dataKey="label" width={130} fontSize={11} tickFormatter={(v: string) => v.length > 20 ? v.slice(0, 19) + '…' : v}/>
                  <Tooltip formatter={(v: any, _n, p: any) => [`${v}% (ผ่าน ${p.payload.ok} / ผิดปกติ ${p.payload.issues})`, 'Uptime']}/>
                  <Bar dataKey="uptime">
                    {d.uptime.map(u => <Cell key={u.itemId} fill={(u.uptime ?? 0) >= 99 ? C.ok : (u.uptime ?? 0) >= 95 ? C.missing : C.issue}/>)}
                    <LabelList dataKey="uptime" position="right" fontSize={11} formatter={(v: any) => `${v}%`}/>
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="text-[11px] text-gray-400 mt-1">สี: เขียว ≥99% · เหลือง ≥95% · แดง &lt;95% (ตัวเลข % แสดงกำกับทุกแท่ง)</p>
        </ChartCard>

        <ChartCard title="เปรียบเทียบ % ความครบถ้วนรายเดือน" rule={d.rules.completeness}>
          {d.monthly.every(m => m.completeness == null) ? <Empty/> : (
            <div className="h-64">
              <ResponsiveContainer>
                <BarChart data={d.monthly.map(m => ({ ...m, label: fmtMonth(m.month) }))} margin={{ left: -20, right: 4, top: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false}/>
                  <XAxis dataKey="label" fontSize={10}/>
                  <YAxis domain={[0, 100]} fontSize={10}/>
                  <Tooltip formatter={(v: any, _n, p: any) => [`${v}% (ตรวจ ${p.payload.ok + p.payload.issue}/${p.payload.required} ช่อง)`, 'ครบถ้วน']}/>
                  <Bar dataKey="completeness" fill={C.neutral}><LabelList dataKey="completeness" position="top" fontSize={10} formatter={(v: any) => (v == null ? '' : `${v}%`)}/></Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>
      </div>

      <ChartCard title="Backup success rate" rule={d.rules.backup}>
        {d.backup.byFreq.length === 0 ? <Empty text="ยังไม่มีข้อมูลจาก Group ประเภท Backup"/> : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-1 grid grid-cols-3 lg:grid-cols-1 gap-2">
              {d.backup.byFreq.map(f => (
                <div key={f.freq} className="text-center">
                  <div className="h-28">
                    <ResponsiveContainer>
                      <PieChart>
                        <Pie data={[{ n: 'สำเร็จ', v: f.ok }, { n: 'ไม่สำเร็จ', v: f.issue }, { n: 'ยังไม่ได้ตรวจ', v: f.missing }]} dataKey="v" nameKey="n" innerRadius="60%" outerRadius="90%" startAngle={90} endAngle={-270}>
                          <Cell fill={C.ok}/><Cell fill={C.issue}/><Cell fill={C.missing}/>
                        </Pie>
                        <Tooltip/>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="text-sm font-semibold -mt-1">{FREQ_TH[f.freq]}: {pctStr(f.rate)}</p>
                  <p className="text-[11px] text-gray-500">✓ {f.ok} · ▲ {f.issue} · ○ {f.missing}</p>
                </div>
              ))}
            </div>
            <div className="lg:col-span-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-left text-gray-500 border-b border-gray-100"><th className="py-1.5 pr-2">ระบบ</th><th className="pr-2">ความถี่</th><th className="pr-2">backup สำเร็จล่าสุด</th><th>ไม่สำเร็จติดต่อกัน</th></tr></thead>
                <tbody>
                  {d.backup.latest.map(b => (
                    <tr key={b.itemId} className="border-b border-gray-50">
                      <td className="py-1.5 pr-2 text-gray-800">{b.name}<span className="text-gray-400"> · {b.group}</span></td>
                      <td className="pr-2">{FREQ_TH[b.freq]}</td>
                      <td className="pr-2">{b.lastOk ? fmtDay(b.lastOk) : 'ไม่พบใน 90 วัน'}</td>
                      <td>{b.consecutiveFail > 0 ? <span className="font-semibold text-red-600">▲ {b.consecutiveFail} ครั้ง</span> : <span className="text-green-700">✓ ปกติ</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </ChartCard>

      <ChartCard title="Timeline ความผิดปกติต่อเนื่อง" rule="ช่วงที่รายการเดียวกันผิดปกติติดกันตั้งแต่ 2 ช่วงเวลาขึ้นไป (วัน/สัปดาห์/เดือนตามความถี่ของ Group)">
        {d.timeline.length === 0 ? <Empty text="ไม่มีปัญหาที่ค้างต่อเนื่องในช่วงที่เลือก ✓"/> : (
          <div className="space-y-1.5">
            <div className="flex justify-between text-[10px] text-gray-400 pl-[180px]"><span>{fmtDay(win.from)}</span><span>{fmtDay(win.to)}</span></div>
            {d.timeline.map((r, i) => (
              <div key={`${r.itemId}${r.from}${i}`} className="flex items-center gap-2 text-xs">
                <div className="w-[172px] flex-shrink-0 truncate text-gray-700" title={`${r.hostName ? r.hostName + ' › ' : ''}${r.itemName} · ${r.groupName}`}>{r.hostName ? `${r.hostName} › ` : ''}{r.itemName}</div>
                <div className="relative flex-1 h-6 bg-gray-50 rounded">
                  <div className="absolute top-0 h-6 rounded text-white text-[10px] flex items-center px-1.5 whitespace-nowrap overflow-hidden"
                    style={{ left: `${pos(r.from)}%`, width: `${Math.max(2, pos(r.to) - pos(r.from) + 100 / days)}%`, background: C.issue }}>
                    ▲ {r.label ?? ''} {fmtDay(r.from)}–{fmtDay(r.to)} ({r.length}){r.ongoing ? ' ● ค้างอยู่' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </ChartCard>
    </div>
  )
}

const now = new Date()

export default function CheckDashboard({ isSuperAdmin, activeCompanyId }: { isSuperAdmin: boolean; activeCompanyId: string | null }) {
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [range, setRange] = useState(1)
  const [groupId, setGroupId] = useState('')
  const [companyId, setCompanyId] = useState('')
  const [heatGroup, setHeatGroup] = useState('')

  const { data: companies = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ['masterdata', 'companies'], queryFn: () => api.get('/masterdata/companies').then(r => r.data), enabled: isSuperAdmin && !activeCompanyId,
  })
  const params = { year, month, range, groupId: groupId || undefined, companyId: companyId || undefined }
  const { data: d, isLoading, error } = useQuery<DashData>({
    queryKey: ['check-dashboard', params],
    queryFn: () => api.get('/checks/dashboard', { params }).then(r => r.data),
  })

  const ym = `${year}-${String(month).padStart(2, '0')}`
  const heatId = groupId || heatGroup || d?.groups[0]?.id || ''
  const years = Array.from({ length: 5 }, (_, i) => now.getFullYear() - i)

  const exportXlsx = async () => {
    try {
      const r = await api.get('/checks/export/xlsx', { params: { ...params, groupId: heatId }, responseType: 'blob' })
      const url = URL.createObjectURL(r.data); downloadDataUrl(url, `daily-check-${ym}.xlsx`); URL.revokeObjectURL(url)
    } catch (e) { alert(errMsg(e)) }
  }
  const printUrl = `/checks/report/print?${new URLSearchParams({ year: String(year), month: String(month), range: String(range), ...(groupId && { groupId }), ...(companyId && { companyId }), heat: heatId }).toString()}`

  const sel = 'border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm bg-white'
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-3 flex flex-wrap items-center gap-2">
        {isSuperAdmin && !activeCompanyId && (
          <select value={companyId} onChange={e => { setCompanyId(e.target.value); setGroupId('') }} className={sel} aria-label="บริษัท/สาขา">
            <option value="">ทุกบริษัท/สาขา</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
        <select value={groupId} onChange={e => setGroupId(e.target.value)} className={sel} aria-label="Group">
          <option value="">ทุก Group</option>{d?.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <select value={month} onChange={e => setMonth(+e.target.value)} className={sel} aria-label="เดือน">
          {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{['มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'][i]}</option>)}
        </select>
        <select value={year} onChange={e => setYear(+e.target.value)} className={sel} aria-label="ปี">{years.map(y => <option key={y} value={y}>{y}</option>)}</select>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden" role="group" aria-label="ช่วงย้อนหลัง">
          {[1, 3, 6, 12].map(r => (
            <button key={r} onClick={() => setRange(r)} className={`px-2.5 py-1.5 text-sm ${range === r ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{r === 1 ? 'เดือนนี้' : `${r} เดือน`}</button>
          ))}
        </div>
        <div className="ml-auto flex gap-2">
          <button onClick={exportXlsx} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"><FileSpreadsheet size={14}/> Excel</button>
          <a href={printUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50"><Printer size={14}/> PDF (พร้อมกราฟ)</a>
        </div>
      </div>

      {isLoading && <p className="text-center text-gray-400 py-16">กำลังโหลด…</p>}
      {error && <p className="text-center text-red-500 py-16">โหลด Dashboard ไม่สำเร็จ</p>}
      {d && (
        <>
          <DashboardBody d={d}/>
          <ChartCard title={`Heatmap ${ym}`} rule="สีพร้อมสัญลักษณ์: ✓ ผ่าน · ▲ ผิดปกติ · ○ ยังไม่ได้ตรวจ · – ไม่เกี่ยวข้อง — คลิกช่องเพื่อดู remark และประวัติ">
            {!groupId && (
              <select value={heatId} onChange={e => setHeatGroup(e.target.value)} className={`${sel} mb-3`} aria-label="Group ของ Heatmap" data-no-export="1">
                {d.groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            )}
            {heatId ? <CheckGrid groupId={heatId} month={ym} canRecord={false} compact/> : <Empty text="ยังไม่มี Group"/>}
          </ChartCard>
        </>
      )}
    </div>
  )
}
